/* eslint-disable max-lines */

import React from 'react'
import { connect } from 'react-redux'
import * as Drawer from 'app/IntegrationDrawer'
import { addDays, addHours, format, isSameDay } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { LDFlagSet } from 'launchdarkly-js-client-sdk'
import { bindActionCreators } from 'redux'

import Button from 'common/components/button'
import DatePicker from 'common/components/datePicker'
import FormItem from 'common/components/formItem'
import GuidingText from 'common/components/guidingText'
import Loader from 'common/components/loaders/Loader'
import MultiSelect from 'common/components/MultiSelect'
import SingleSelect from 'common/components/singleSelect'
import TimePicker from 'common/components/timePicker'
import Tooltip from 'common/components/Tooltip'
import { Time as TimeIcon } from 'common/icons'

import {
  AsyncThunkDispatch,
  cancelBrazeCampaign,
  fetchBrazeCampaigns,
  fetchCampaignData,
  fetchTimeZone,
  syncBrazeCampaign,
} from '../../../Workflow.actions'

import CancelCard from './CancelCard'

import styles from '../Integrations.module.css'

const TOOLTIP_TEXT = {
  dropdown: 'Connect a Braze campaign for Jacquard to retrieve results',
  startTime: 'This is the time that your Braze campaign is scheduled to start.',
  testEndTime:
    'This is the time that Braze will end the multi-variate test and ' +
    'Jacquard will collect the test results.',
  campaignEndTime:
    'This is the time that Jacquard will collect the results of the final send',
  draft:
    'Jacquard has matched the following draft messages based on their ' +
    'External ID',
  buttonDefault:
    'To ensure accurate test results, make sure these details match Braze.',
}

export interface Props {
  actions: any
  flags: LDFlagSet
  projectId: string
  campaignId: string
  brazeCampaigns: any[]
  isLoadingCampaigns: boolean
  isUpdatingMessages: boolean
  maxMultiselectAmount: number | undefined
  timeZone: string
  distributionChannel: string
  campaignData: any
  closeDrawer: () => void
  footerstyles: React.CSSProperties
  getCampaignData: (campaignId: string) => Promise<void>
  updateBrazeCampaign: (
    data: any,
    campaignId: string,
    callback: () => void
  ) => Promise<void>
}

interface State {
  selectedCampaign: Array<string> | string | undefined
  campaignStartDate: Date | undefined
  campaignStartTime: string | undefined
  campaignEndDate: Date | undefined
  campaignEndTime: string | undefined
  testEndDate: Date | undefined
  testEndTime: string | undefined
  isMultiSelect: boolean
  errorMessages: any
  timeZone: string | undefined
}

export const initialState: State = {
  selectedCampaign: [],
  campaignStartDate: undefined,
  campaignStartTime: undefined,
  campaignEndDate: undefined,
  campaignEndTime: undefined,
  testEndDate: undefined,
  testEndTime: undefined,
  isMultiSelect: false,
  errorMessages: {},
  timeZone: undefined,
}

export class BrazeIntegration extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = initialState
  }

  componentDidMount() {
    const {
      actions,
      projectId,
      maxMultiselectAmount,
      distributionChannel,
      campaignData,
    } = this.props

    const selectedBrazeCampaigns =
      campaignData?.campaign_data?.braze_campaign_id || []
    if (projectId && distributionChannel) {
      actions.fetchBrazeCampaigns(
        projectId,
        distributionChannel,
        selectedBrazeCampaigns
      )
    }
    if (projectId) {
      actions.fetchTimeZone(projectId)
    }
    this.setState({
      isMultiSelect: !!(maxMultiselectAmount && maxMultiselectAmount > 1),
    })
  }

  componentDidUpdate() {
    const { timeZone: propsTimeZone, campaignData } = this.props
    const { timeZone: stateTimeZone } = this.state

    if (
      propsTimeZone &&
      typeof propsTimeZone === 'string' &&
      !stateTimeZone &&
      campaignData
    ) {
      const selectedBrazeCampaigns =
        campaignData?.campaign_data?.braze_campaign_id || []
      const nowInTz = toZonedTime(new Date(), propsTimeZone)
      const campaignStartRaw = campaignData?.campaign_data?.campaign_start_time
      const optimizationEndRaw =
        campaignData?.campaign_data?.optimization_end_time
      const campaignEndRaw = campaignData?.campaign_data?.campaign_end_time

      const campaignStartDate = campaignStartRaw
        ? toZonedTime(new Date(campaignStartRaw), propsTimeZone)
        : nowInTz
      const campaignStartTime = campaignStartRaw
        ? format(
            toZonedTime(new Date(campaignStartRaw), propsTimeZone),
            'HH:mm'
          )
        : format(nowInTz, 'HH:mm')
      const testEndDate = optimizationEndRaw
        ? toZonedTime(new Date(optimizationEndRaw), propsTimeZone)
        : toZonedTime(addHours(new Date(), 1), propsTimeZone)
      const testEndTime = optimizationEndRaw
        ? format(
            toZonedTime(new Date(optimizationEndRaw), propsTimeZone),
            'HH:mm'
          )
        : format(toZonedTime(addHours(new Date(), 1), propsTimeZone), 'HH:mm')
      const campaignEndDate = campaignEndRaw
        ? toZonedTime(new Date(campaignEndRaw), propsTimeZone)
        : toZonedTime(addDays(new Date(), 4), propsTimeZone)
      const campaignEndTime = campaignEndRaw
        ? format(toZonedTime(new Date(campaignEndRaw), propsTimeZone), 'HH:mm')
        : format(toZonedTime(addDays(new Date(), 4), propsTimeZone), 'HH:mm')

      this.setState({
        timeZone: propsTimeZone,
        selectedCampaign: selectedBrazeCampaigns,
        campaignStartDate,
        campaignStartTime,
        testEndDate,
        testEndTime,
        campaignEndDate,
        campaignEndTime,
      })
    }
  }

  componentWillUnmount() {
    const { campaignId, getCampaignData } = this.props
    getCampaignData(campaignId)
  }

  onChangeUpdateState = (val: any, type: string) => {
    const { maxMultiselectAmount } = this.props
    const { selectedCampaign, isMultiSelect } = this.state

    let campaigns: Array<string>

    if (type === 'campaign' && maxMultiselectAmount && isMultiSelect) {
      campaigns = val.length <= maxMultiselectAmount ? val : selectedCampaign
    }
    if (type === 'campaign' && !isMultiSelect && val) {
      campaigns = [val]
    }
    let campaignEndDateTime: Date | undefined =
      type === 'campaign_end_date' ? val : undefined
    let campaignTestEndTime: Date | undefined =
      type === 'test_end_date' ? val : undefined
    if (type === 'campaign_start_date') {
      campaignEndDateTime = val ? addDays(val as Date, 4) : undefined
      campaignTestEndTime = val
    }
    this.setState((state) => {
      return {
        ...state,
        ...(type === 'campaign' && { selectedCampaign: campaigns }),
        ...(type === 'campaign_start_date' && { campaignStartDate: val }),
        ...(type === 'campaign_start_time' && { campaignStartTime: val }),
        ...((type === 'campaign_end_date' ||
          type === 'campaign_start_date') && {
          campaignEndDate: campaignEndDateTime,
        }),
        ...(type === 'campaign_end_time' && { campaignEndTime: val }),
        ...((type === 'test_end_date' || type === 'campaign_start_date') && {
          testEndDate: campaignTestEndTime,
        }),
        ...(type === 'test_end_time' && { testEndTime: val }),
      }
    })
  }

  disableDate = (current: any, compareStartDate = false) => {
    const { campaignStartDate, timeZone } = this.state
    if (timeZone && current instanceof Date) {
      let dateToCompare = new Date().toLocaleDateString('sv-SE', { timeZone })
      if (compareStartDate && campaignStartDate) {
        dateToCompare = format(
          toZonedTime(campaignStartDate, timeZone),
          'yyyy-MM-dd'
        )
      }
      return current && current.toLocaleDateString('sv-SE') < dateToCompare
    }
    return false
  }

  getDisabledHours = (): any => {
    return []
  }

  getTestTimeDisabledHours = (): any => {
    return []
  }

  getCampaignEndDisabledHours = (): any => {
    return []
  }

  getDisabledHoursArray = (
    _dateFormatted: any,
    _timeFormatted: any,
    _allowSameTime?: boolean
  ) => {
    return []
  }

  getDisabledMinutesArray = (
    _selectedHour: any,
    _dateFormatted: any,
    _timeFormatted: any
  ) => {
    return []
  }

  getDisabledMinutes = (_selectedHour: number): any => {
    return []
  }

  getTestTimeDisabledMinutes = (_selectedHour: number): any => {
    return []
  }

  getCampaignEndDisabledMinutes = (_selectedHour: number): any => {
    return []
  }

  validateInputsOnSubmit = () => {
    const {
      selectedCampaign,
      campaignStartDate,
      campaignStartTime,
      campaignEndDate,
      campaignEndTime,
      testEndDate,
      testEndTime,
      timeZone,
    } = this.state
    const errorMessages = {
      selectedCampaign: '',
      campaignStartDate: '',
      campaignStartTime: '',
      campaignEndDate: '',
      campaignEndTime: '',
      testEndDate: '',
      testEndTime: '',
    }

    let isFormValid = true

    if (!selectedCampaign || selectedCampaign.length === 0) {
      isFormValid = false
      errorMessages.selectedCampaign = 'This field is required'
    }
    if (!campaignStartDate || !campaignStartTime) {
      isFormValid = false
      errorMessages.campaignStartDate =
        'Experiment start date and time fields are required'
    }
    if (!campaignEndDate || !campaignEndTime) {
      isFormValid = false
      errorMessages.campaignEndDate =
        'Experiment end date and time fields are required'
    }
    if (!testEndDate || !testEndTime) {
      isFormValid = false
      errorMessages.testEndDate =
        'Experiment test end date and time fields are required'
    }
    if (
      campaignStartDate &&
      campaignStartTime &&
      campaignEndDate &&
      campaignEndTime &&
      testEndDate &&
      testEndTime
    ) {
      const [startHr, startMin] = campaignStartTime.split(':').map(Number)
      const [testHr, testMin] = testEndTime.split(':').map(Number)
      const [endHr, endMin] = campaignEndTime.split(':').map(Number)

      const startDateTime = new Date(campaignStartDate)
      startDateTime.setHours(startHr, startMin, 0, 0)
      const testDateTime = new Date(testEndDate)
      testDateTime.setHours(testHr, testMin, 0, 0)
      const endDateTime = new Date(campaignEndDate)
      endDateTime.setHours(endHr, endMin, 0, 0)

      if (
        timeZone &&
        isSameDay(startDateTime, toZonedTime(new Date(), timeZone)) &&
        this.disableDate(campaignStartDate, false)
      ) {
        errorMessages.campaignStartDate = 'This time is not available'
        isFormValid = false
      }
      if (this.disableDate(testDateTime, true)) {
        errorMessages.testEndDate = 'This date is not available'
        isFormValid = false
      }
      const startPlus60 = addHours(startDateTime, 1)
      if (startPlus60 > testDateTime) {
        errorMessages.testEndDate =
          'Test time should be at least 1 hour greather than start time'
        isFormValid = false
      }
      if (testDateTime > endDateTime) {
        errorMessages.campaignEndDate =
          'Experiment end time cannot be less than test time'
        isFormValid = false
      }
    }

    this.setState({ errorMessages })
    return isFormValid
  }

  handleSubmit = async () => {
    if (!this.validateInputsOnSubmit()) {
      return
    }
    const {
      selectedCampaign,
      campaignStartDate,
      campaignStartTime,
      campaignEndDate,
      campaignEndTime,
      testEndDate,
      testEndTime,
    } = this.state

    const { campaignId, closeDrawer, updateBrazeCampaign } = this.props

    const buildDateTimeISO = (
      date: Date | undefined,
      timeStr: string | undefined
    ): string => {
      if (!date || !timeStr) {
        return ''
      }
      const [hr, min] = timeStr.split(':').map(Number)
      const dt = new Date(date)
      dt.setHours(hr, min, 0, 0)
      return dt.toISOString()
    }

    const campaignStartDateFormatted = buildDateTimeISO(
      campaignStartDate,
      campaignStartTime
    )
    const campaignEndDateFormatted = buildDateTimeISO(
      campaignEndDate,
      campaignEndTime
    )
    const testEndDateFormatted = buildDateTimeISO(testEndDate, testEndTime)
    const payloadData = {
      braze_campaign_id: selectedCampaign,
      campaign_start_time: campaignStartDateFormatted,
      campaign_end_time: campaignEndDateFormatted,
      optimization_start_time: campaignStartDateFormatted,
      optimization_end_time: testEndDateFormatted,
    }

    updateBrazeCampaign(payloadData, campaignId, closeDrawer)
  }

  render() {
    const {
      selectedCampaign,
      campaignStartDate,
      campaignStartTime,
      campaignEndDate,
      campaignEndTime,
      testEndDate,
      testEndTime,
      isMultiSelect,
      errorMessages,
      timeZone,
    } = this.state

    const {
      brazeCampaigns,
      isLoadingCampaigns,
      maxMultiselectAmount,
      isUpdatingMessages,
      campaignData,
      actions,
      campaignId,
      flags,
    } = this.props

    return (
      <>
        <Drawer.Content>
          <div className="updated-component">
            <Loader isLoading={isLoadingCampaigns || !timeZone}>
              <FormItem
                label="Braze campaign"
                error={errorMessages.selectedCampaign || undefined}
                className="required"
              >
                {isMultiSelect ? (
                  <MultiSelect
                    maxOptions={maxMultiselectAmount}
                    data-testid="SelectMatch"
                    placeholder="Select a campaign"
                    name="campaigns"
                    className={styles.integrationSelect}
                    onApply={(val) =>
                      val?.length > 0 &&
                      this.onChangeUpdateState(val, 'campaign')
                    }
                    selectedItems={
                      selectedCampaign
                        ? Array.isArray(selectedCampaign)
                          ? selectedCampaign
                          : [selectedCampaign]
                        : []
                    }
                    items={brazeCampaigns?.map((campaign) => ({
                      label: campaign.braze_campaign_name,
                      value: campaign.braze_campaign_name,
                      id: campaign.braze_campaign_id,
                    }))}
                    menuPortalTarget={document.body}
                  />
                ) : (
                  <SingleSelect
                    isClearable
                    isSearchable
                    data-testid="SelectMatch"
                    placeholder="Select a campaign"
                    menuPortalTarget={document.body}
                    menuShouldBlockScroll={true}
                    className={styles.integrationSelect}
                    onChange={(val) =>
                      this.onChangeUpdateState(val?.value, 'campaign')
                    }
                    value={
                      Array.isArray(selectedCampaign)
                        ? selectedCampaign[0]
                        : selectedCampaign
                    }
                    options={brazeCampaigns?.map((campaign) => ({
                      label: campaign.braze_campaign_name,
                      value: campaign.braze_campaign_id,
                    }))}
                  />
                )}
                <GuidingText text={TOOLTIP_TEXT.dropdown} className="mb-7" />
              </FormItem>
              {timeZone && (
                <p className="text-gray-700 font-medium" data-testid="timeZone">
                  Braze Account Timezone - {timeZone}
                </p>
              )}
              <p className="text-gray-400 mt-1 mb-8">
                Input your experiment time details below based on your Braze
                Account Timezone shown.
              </p>
              <FormItem
                label="Experiment start time"
                className="required"
                error={
                  errorMessages.campaignStartDate ||
                  errorMessages.campaignStartTime ||
                  undefined
                }
              >
                <div className="flex gap-4">
                  <Tooltip
                    overlay={TOOLTIP_TEXT.startTime}
                    trigger="focus"
                    placement="left"
                    overlayStyle={{ maxWidth: 226 }}
                  >
                    <DatePicker
                      id="date-picker"
                      numberOfMonths={1}
                      verticalHeight={372}
                      isTodayVisible={true}
                      disabled={
                        !timeZone ||
                        !selectedCampaign ||
                        !selectedCampaign.length
                      }
                      isOutsideRange={(current: Date): boolean =>
                        this.disableDate(current, false)
                      }
                      data-testid="startDatePicker"
                      ariaLabel="startDatePicker"
                      date={campaignStartDate ?? undefined}
                      showClearDate={false}
                      onDateChange={(date: Date | null) => {
                        this.onChangeUpdateState(
                          date ?? null,
                          'campaign_start_date'
                        )
                      }}
                    />
                  </Tooltip>
                  <Tooltip overlay="Time" placement="right">
                    <TimePicker
                      disabled={
                        !timeZone ||
                        !selectedCampaign ||
                        !selectedCampaign.length ||
                        !campaignStartDate
                      }
                      disabledHours={() => this.getDisabledHours()}
                      allowClear={false}
                      disabledMinutes={(selectedHour) => {
                        return this.getDisabledMinutes(selectedHour)
                      }}
                      data-testid="startTimePicker"
                      className={styles.integrationTimePicker}
                      format="HH:mm"
                      suffixIcon={<TimeIcon size={6} />}
                      minuteStep={15}
                      value={campaignStartTime ?? null}
                      onChange={(val: string | null) =>
                        this.onChangeUpdateState(
                          val ?? undefined,
                          'campaign_start_time'
                        )
                      }
                    />
                  </Tooltip>
                </div>
              </FormItem>
              <FormItem
                label="Test end time"
                className="required"
                error={
                  errorMessages.testEndDate ||
                  errorMessages.testEndTime ||
                  undefined
                }
              >
                <div className="flex gap-4">
                  <Tooltip
                    overlay={TOOLTIP_TEXT.testEndTime}
                    trigger="focus"
                    placement="left"
                    overlayStyle={{ maxWidth: 226 }}
                  >
                    <DatePicker
                      id="date-picker"
                      numberOfMonths={1}
                      verticalHeight={372}
                      isTodayVisible={true}
                      disabled={
                        !timeZone ||
                        !selectedCampaign ||
                        !selectedCampaign.length ||
                        !campaignStartDate ||
                        !campaignStartTime
                      }
                      isOutsideRange={(current: Date): boolean =>
                        this.disableDate(current, true)
                      }
                      showClearDate={false}
                      data-testid="testDatePicker"
                      date={testEndDate ?? undefined}
                      onDateChange={(date: Date | null) => {
                        this.onChangeUpdateState(date ?? null, 'test_end_date')
                      }}
                    />
                  </Tooltip>
                  <Tooltip overlay="Time" placement="right">
                    <TimePicker
                      disabled={
                        !timeZone ||
                        !selectedCampaign ||
                        !selectedCampaign.length ||
                        !campaignStartDate ||
                        !campaignStartTime ||
                        !testEndDate
                      }
                      allowClear={false}
                      disabledHours={() => this.getTestTimeDisabledHours()}
                      disabledMinutes={(selectedHour) => {
                        return this.getTestTimeDisabledMinutes(selectedHour)
                      }}
                      data-testid="testTimePicker"
                      className={styles.integrationTimePicker}
                      format="HH:mm"
                      suffixIcon={<TimeIcon size={6} />}
                      minuteStep={15}
                      value={testEndTime ?? null}
                      onChange={(val: string | null) =>
                        this.onChangeUpdateState(
                          val ?? undefined,
                          'test_end_time'
                        )
                      }
                    />
                  </Tooltip>
                </div>
              </FormItem>
              <FormItem
                label="Experiment end time"
                className="required"
                error={
                  errorMessages.campaignEndDate ||
                  errorMessages.campaignEndTime ||
                  undefined
                }
              >
                <div className="flex gap-4">
                  <Tooltip
                    overlay={TOOLTIP_TEXT.campaignEndTime}
                    trigger="focus"
                    placement="left"
                    overlayStyle={{ maxWidth: 226 }}
                  >
                    <DatePicker
                      id="date-picker"
                      numberOfMonths={1}
                      verticalHeight={372}
                      isTodayVisible={true}
                      disabled={
                        !timeZone ||
                        !selectedCampaign ||
                        !selectedCampaign.length ||
                        !campaignStartDate ||
                        !campaignStartTime ||
                        !testEndDate ||
                        !testEndTime
                      }
                      showClearDate={false}
                      isOutsideRange={(current: Date): boolean =>
                        this.disableDate(current, true)
                      }
                      data-testid="endDatePicker"
                      ariaLabel="endDatePicker"
                      date={campaignEndDate ?? undefined}
                      onDateChange={(date: Date | null) => {
                        this.onChangeUpdateState(
                          date ?? null,
                          'campaign_end_date'
                        )
                      }}
                    />
                  </Tooltip>
                  <Tooltip overlay="Time" placement="right">
                    <TimePicker
                      disabled={
                        !timeZone ||
                        !selectedCampaign ||
                        !selectedCampaign.length ||
                        !campaignStartDate ||
                        !campaignStartTime ||
                        !testEndDate ||
                        !testEndTime ||
                        !campaignEndDate
                      }
                      allowClear={false}
                      data-testid="endTimePicker"
                      disabledHours={() => this.getCampaignEndDisabledHours()}
                      disabledMinutes={(selectedHour) => {
                        return this.getCampaignEndDisabledMinutes(selectedHour)
                      }}
                      className={styles.integrationTimePicker}
                      format="HH:mm"
                      suffixIcon={<TimeIcon size={6} />}
                      minuteStep={15}
                      value={campaignEndTime ?? null}
                      onChange={(val: string | null) =>
                        this.onChangeUpdateState(
                          val ?? undefined,
                          'campaign_end_time'
                        )
                      }
                    />
                  </Tooltip>
                </div>
              </FormItem>
              {flags.showBrazeCancelBtn &&
                campaignData?.campaign_data?.braze_campaign_id &&
                campaignData?.campaign_data?.campaign_end_time &&
                campaignData?.campaign_data?.campaign_start_time &&
                campaignData?.campaign_data?.optimization_end_time &&
                campaignData?.campaign_data?.optimization_start_time && (
                  <CancelCard
                    onCancel={() =>
                      actions.cancelBrazeCampaign(campaignId, () => {
                        this.setState(initialState)
                      })
                    }
                  />
                )}
            </Loader>
          </div>
        </Drawer.Content>
        <Drawer.Footer>
          <Tooltip
            overlay={TOOLTIP_TEXT.buttonDefault}
            trigger="hover"
            placement="right"
            overlayStyle={{ maxWidth: 226 }}
          >
            <Button
              data-cy="braze-save-button"
              variant="primary"
              disabled={isLoadingCampaigns}
              loading={isUpdatingMessages}
              onClick={() => this.handleSubmit()}
              data-testid="submit"
            >
              Save
            </Button>
          </Tooltip>
        </Drawer.Footer>
      </>
    )
  }
}

export function mapDispatchToProps(dispatch: AsyncThunkDispatch) {
  return {
    getCampaignData: async (campaignId: string) => {
      await dispatch(fetchCampaignData(campaignId))
    },
    updateBrazeCampaign: async (
      data: any,
      campaignId: string,
      callback: () => void
    ) => {
      await dispatch(syncBrazeCampaign(data, campaignId))
      callback()
    },
    actions: bindActionCreators(
      {
        fetchBrazeCampaigns,
        fetchTimeZone,
        cancelBrazeCampaign,
      },
      dispatch
    ),
  }
}

export function mapStateToProps(state: any, ownsProps: any) {
  const {
    currentCampaignId,
    projectId,
    brazeCampaigns,
    campaignData: {
      campaign_configuration: {
        distribution_channel,
        integration_options: { max_multiselect_amount },
      },
    },
    isWaitingState: { isWaitingFor, isWaiting },
    campaignData,
    timeZone,
  } = state.campaignStates

  return {
    campaignId: currentCampaignId,
    projectId,
    brazeCampaigns,
    isLoadingCampaigns: isWaitingFor === 'fetchingBrazeCampaigns' && isWaiting,
    maxMultiselectAmount: max_multiselect_amount,
    timeZone,
    distributionChannel: distribution_channel,
    campaignData,
    closeDrawer: ownsProps.closeDrawer,
    flags: state.flagStates.flags,
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(BrazeIntegration)
